import React, { useState } from "react"
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  useTheme,
  Divider
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useResponsive } from "src/utils/useResponsive"
import { useTranslation } from "react-i18next"

interface PageInfoModalProps {
  name: string
  open: boolean
  onClose: () => void
  onDontShowAgain: (dontShow: boolean) => void
}

const PageInfoModal: React.FC<PageInfoModalProps> = ({ open, onClose, onDontShowAgain, name }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const { isMobile } = useResponsive()
  const { palette } = useTheme()
  const { t } = useTranslation("tabletInfo")

  const handleClose = () => {
    if (dontShowAgain) {
      onDontShowAgain(true)
    }
    onClose()
    setDontShowAgain(false)
  }

  const handleDontShowChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDontShowAgain(event.target.checked)
  }

  // Only show on mobile
  if (!isMobile) {
    return null
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: "#E6FBFF",
          borderRadius: 0
        }
      }}
    >
      <DialogContent
        sx={{
          p: 4,
          pt: 15,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "16px",
            right: "16px",
            color: palette.text.primary,
            padding: "8px",
            zIndex: 1
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>

        {/* Content Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            justifyContent: "space-between"
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
            {/* Icon */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
                marginTop: "16px"
              }}
            >
              <Box
                component={"img"}
                src={t(`${name}.iconPath`) as string}
                alt=""
                sx={{
                  width: {
                    xs: "80px"
                  }
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1E293B",
                marginBottom: "16px",
                fontSize: "24px",
                lineHeight: "32px"
              }}
            >
              {t(`${name}.title`)}
            </Typography>

            {/* Description */}
            <Typography variant="body1">{t(`${name}.description`)}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1">{t(`${name}.description2`)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={dontShowAgain} onChange={handleDontShowChange} />}
              label={<Typography sx={{ fontWeight: 500 }}> {"Don't show again"} </Typography>}
              sx={{ margin: 0, backgroundColor: "white", borderRadius: "30px", py: 1, px: 3 }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default PageInfoModal
